import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMqttTopic } from '../mqtt.jsx'
import { useAdminAuth } from '../adminAuth.jsx'
import {
  logout as apiLogout, listSessions, updateSession, resetSession, listRuns, updateRun,
  listNonEntrantRuns, deleteNonEntrantRuns,
  clearDown, sendResultsEmail, listReplayEvents, startReplay, stopReplay, getReplayStatus,
} from '../adminApi.js'
import { VALID_SESSION_TYPES } from '../sessions.js'

function SessionsPanel({ hill, date }) {
  const [sessions, setSessions] = useState(null)
  const [error, setError] = useState('')
  const [drafts, setDrafts] = useState({}) // code -> {type, label}
  const [saving, setSaving] = useState(null)

  const load = () => {
    if (!hill || !date) return
    listSessions(hill, date)
      .then((data) => {
        setSessions(data.sessions)
        setDrafts(Object.fromEntries(data.sessions.map((s) => [s.session_code, { type: s.type, label: s.label }])))
        setError('')
      })
      .catch((e) => setError(e.message))
  }

  useEffect(load, [hill, date])

  const setDraft = (code, patch) => setDrafts((d) => ({ ...d, [code]: { ...d[code], ...patch } }))

  const save = async (code) => {
    setSaving(code)
    try {
      await updateSession(hill, date, { session_code: code, ...drafts[code] })
      load()
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(null)
    }
  }

  const resetToAuto = async (code) => {
    if (!window.confirm(`Reset "${code}" back to auto-detected type/label? This discards the admin override.`)) return
    setSaving(code)
    try {
      await resetSession(hill, date, code)
      load()
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(null)
    }
  }

  return (
    <div className="admin-panel">
      <h3>Sessions</h3>
      <div className="desc">
        We guess Practice / Timed / Open / Top&nbsp;Ten automatically from where a session falls in the event.
        Override the type if we guessed wrong — display label is cosmetic only and never affects scoring or records.
      </div>
      {error && <div className="empty-state" style={{ color: 'var(--red)' }}>{error}</div>}
      {!sessions ? (
        <div className="empty-state">Loading…</div>
      ) : sessions.length === 0 ? (
        <div className="empty-state">No sessions detected yet for this event.</div>
      ) : (
        <>
          <div className="session-row session-row-head">
            <div></div><div>Type</div><div>Display label</div><div></div>
          </div>
          {sessions.map((s) => {
            const draft = drafts[s.session_code] || { type: s.type, label: s.label }
            const dirty = draft.type !== s.type || draft.label !== s.label
            return (
              <div className="session-row" key={s.session_code}>
                <div className="code-badge">{s.session_code}</div>
                <div className="session-type-cell">
                  <select value={draft.type} onChange={(e) => setDraft(s.session_code, { type: e.target.value })}>
                    {VALID_SESSION_TYPES.map((t) => (
                      <option key={t} value={t}>{t[0].toUpperCase() + t.slice(1)}</option>
                    ))}
                  </select>
                  <span className={`guess-badge ${s.guessed ? '' : 'override'}`}>{s.guessed ? 'auto' : 'admin-set'}</span>
                  {!s.guessed && (
                    <button
                      type="button"
                      className="reset-to-auto-link"
                      disabled={saving === s.session_code}
                      onClick={() => resetToAuto(s.session_code)}
                      title="Discard the admin override and re-guess type/label automatically"
                    >
                      reset to auto
                    </button>
                  )}
                </div>
                <input
                  type="text"
                  value={draft.label}
                  onChange={(e) => setDraft(s.session_code, { label: e.target.value })}
                />
                <button className="btn-sm save" disabled={!dirty || saving === s.session_code} onClick={() => save(s.session_code)}>
                  {saving === s.session_code ? 'Saving…' : 'Save'}
                </button>
              </div>
            )
          })}
        </>
      )}
    </div>
  )
}

// '' / null / undefined all mean "no value entered" - treated as null for
// comparison, so a blank gate field the admin never touched doesn't read as
// Number('') === 0 and register as a spurious correction.
const numOrNull = (v) => (v === '' || v === undefined || v === null ? null : Number(v))
const numChanged = (draftVal, rawVal) => numOrNull(draftVal) !== (rawVal ?? null)

function RunsPanel({ hill, date }) {
  const [raceNumberInput, setRaceNumberInput] = useState('')
  const [raceNumber, setRaceNumber] = useState('')
  // Bumped on every lookup submission, even a repeat of the same race number -
  // raceNumber alone can't drive the effect below, since setting state to a
  // value equal to its current value is a no-op in React (the effect simply
  // wouldn't re-fire), which left a re-search of the same number stuck
  // showing "Loading…" forever with no way to recover except typing a
  // different number first.
  const [searchSeq, setSearchSeq] = useState(0)
  const [runs, setRuns] = useState(null)
  const [error, setError] = useState('')
  const [drafts, setDrafts] = useState({}) // key -> {finish_time, flagged, deleted, gates: {field: value}}
  // The effective (already-saved) value per row, separate from raw_finish_time/
  // raw_gates - "dirty" and the save diff must compare against what's actually
  // saved, not the pre-correction raw value, or the Save button stays lit
  // forever on a row that already has a correction applied.
  const [baselines, setBaselines] = useState({})
  const [saving, setSaving] = useState(null)

  const keyOf = (r) => `${r.race_number}|${r.run_type_raw}|${r.run_number}`

  const load = (rn) => {
    if (!hill || !date || !rn) return
    setRuns(null)
    setError('')
    listRuns(hill, date, rn)
      .then((data) => {
        setRuns(data.runs)
        const effective = Object.fromEntries(data.runs.map((r) => [keyOf(r), {
          finish_time: r.override_fields?.finish_time ?? r.raw_finish_time,
          flagged: r.override_flag ?? r.raw_flag,
          deleted: r.deleted,
          gates: Object.fromEntries(Object.keys(r.raw_gates || {}).map(
            (f) => [f, r.override_fields?.[f] ?? r.raw_gates[f]]
          )),
        }]))
        setBaselines(effective)
        setDrafts(effective)
      })
      .catch((e) => setError(e.message))
  }

  useEffect(() => { load(raceNumber) }, [hill, date, raceNumber, searchSeq])

  // Union of every gate field present on any run for this race number - so
  // e.g. a Practice run missing speed1 doesn't cost a blank column that no
  // run actually has data for, but a rider with one Gurston-timed run still
  // gets S1/Int./K-A columns for their other runs too.
  const gateFields = Array.from(new Set((runs || []).flatMap((r) => Object.keys(r.raw_gates || {}))))

  const setDraft = (key, patch) => setDrafts((d) => ({ ...d, [key]: { ...d[key], ...patch } }))
  const setDraftGate = (key, field, value) =>
    setDrafts((d) => ({ ...d, [key]: { ...d[key], gates: { ...d[key]?.gates, [field]: value } } }))

  const submitRaceNumber = (e) => {
    e.preventDefault()
    setRaceNumber(raceNumberInput.trim())
    setSearchSeq((n) => n + 1)
  }

  const save = async (r) => {
    const key = keyOf(r)
    const draft = drafts[key]
    setSaving(key)
    try {
      const override_fields = {}
      if (numChanged(draft.finish_time, r.raw_finish_time)) override_fields.finish_time = numOrNull(draft.finish_time)
      for (const f of gateFields) {
        if (numChanged(draft.gates?.[f], r.raw_gates?.[f])) override_fields[f] = numOrNull(draft.gates[f])
      }
      await updateRun(hill, date, {
        race_number: r.race_number, run_type_raw: r.run_type_raw, run_number: r.run_number,
        override_fields,
        override_flag: draft.flagged !== r.raw_flag ? draft.flagged : null,
        deleted: draft.deleted,
      })
      load(raceNumber)
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(null)
    }
  }

  return (
    <div className="admin-panel">
      <h3>Correct/Delete a Run (Alpha Testing)</h3>
      <div className="desc">
        Fix a finish time, split/speed reading, or flag status, or delete a run entirely — takes
        effect on the next poll (~30s). Look up one race number at a time.
      </div>
      <form onSubmit={submitRaceNumber} className="toolbar" style={{ alignItems: 'flex-end' }}>
        <div className="field" style={{ margin: 0, width: 140 }}>
          <label>Race number</label>
          <input
            type="text" inputMode="numeric" value={raceNumberInput}
            onChange={(e) => setRaceNumberInput(e.target.value)}
            placeholder="e.g. 55"
          />
        </div>
        <button className="btn-sm save" type="submit" disabled={!raceNumberInput.trim()}>Look up</button>
      </form>
      {error && <div className="empty-state" style={{ color: 'var(--red)' }}>{error}</div>}
      {!raceNumber || error ? null : !runs ? (
        <div className="empty-state">Loading…</div>
      ) : runs.length === 0 ? (
        <div className="empty-state">No runs recorded yet for race number {raceNumber} at this event.</div>
      ) : (
        <div className="table-wrap correction-table" style={{ boxShadow: 'none' }}>
          <table>
            <thead>
              <tr>
                <th>#</th><th>Rider</th><th>Session</th>
                {gateFields.map((f) => <th key={f} className="num-col mono">{f}</th>)}
                <th className="num-col">Finish</th><th>Flag</th><th className="sticky-actions"></th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => {
                const key = keyOf(r)
                const draft = drafts[key] || {}
                const base = baselines[key] || {}
                const dirty = numChanged(draft.finish_time, base.finish_time) ||
                  draft.flagged !== base.flagged || draft.deleted !== base.deleted ||
                  gateFields.some((f) => numChanged(draft.gates?.[f], base.gates?.[f] ?? null))
                return (
                  <tr key={key} className={draft.deleted ? 'dirty' : ''} style={draft.deleted ? { opacity: 0.5 } : undefined}>
                    <td className="mono">#{r.race_number}</td>
                    <td>{r.rider_first} {r.rider_last}</td>
                    <td className="mono">{r.session_code}</td>
                    {gateFields.map((f) => (
                      <td key={f} className="num-col">
                        <input
                          type="text"
                          value={draft.gates?.[f] ?? ''}
                          onChange={(e) => setDraftGate(key, f, e.target.value)}
                        />
                      </td>
                    ))}
                    <td className="num-col">
                      <input
                        type="text"
                        value={draft.finish_time ?? ''}
                        onChange={(e) => setDraft(key, { finish_time: e.target.value })}
                      />
                    </td>
                    <td>
                      <button
                        className={`flag-toggle ${draft.flagged ? 'on' : ''}`}
                        onClick={() => setDraft(key, { flagged: !draft.flagged })}
                      >
                        {draft.flagged ? 'Flagged' : 'Clean'}
                      </button>
                    </td>
                    <td className="sticky-actions">
                      <button className="icon-btn" title={draft.deleted ? 'Restore' : 'Delete'}
                        onClick={() => setDraft(key, { deleted: !draft.deleted })}>
                        {draft.deleted ? '↺' : '✕'}
                      </button>
                      <button className="btn-sm save" disabled={!dirty || saving === key} onClick={() => save(r)}>
                        {saving === key ? 'Saving…' : 'Save'}
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function NonEntrantRunsPanel({ hill, date }) {
  const [riders, setRiders] = useState(null)
  const [checking, setChecking] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState('')

  const check = async () => {
    setChecking(true)
    setError('')
    setResult('')
    setRiders(null)
    try {
      const data = await listNonEntrantRuns(hill, date)
      setRiders(data.riders)
    } catch (e) {
      setError(e.message)
    } finally {
      setChecking(false)
    }
  }

  const remove = async () => {
    if (!window.confirm(
      `Delete ${riders.reduce((n, r) => n + r.run_count, 0)} run(s) for ${riders.length} rider(s) ` +
      "not in this event's entry list? This can't be undone."
    )) return
    setDeleting(true)
    setError('')
    try {
      const data = await deleteNonEntrantRuns(hill, date)
      setResult(`Deleted ${data.deleted_runs} run(s).`)
      setRiders(null)
    } catch (e) {
      setError(e.message)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="admin-panel">
      <h3>Remove non-entrant runs</h3>
      <div className="desc">
        A shared-venue meeting (e.g. a car event sharing the venue that day) can end up in
        live_runs if the live poll's own entry-list filter ever failed for this event. Checks
        for race numbers in live_runs that aren't in this event's entry_list.
      </div>
      <button className="btn-sm save" disabled={!hill || !date || checking} onClick={check}>
        {checking ? 'Checking…' : 'Check'}
      </button>
      {error && <div className="empty-state" style={{ color: 'var(--red)' }}>{error}</div>}
      {result && <div className="magic-sent">{result}</div>}
      {riders && (
        riders.length === 0 ? (
          <div className="empty-state">No non-entrant runs found.</div>
        ) : (
          <>
            <div className="table-wrap" style={{ boxShadow: 'none', marginTop: 14 }}>
              <table>
                <thead>
                  <tr><th>#</th><th>Rider</th><th className="num-col">Runs</th></tr>
                </thead>
                <tbody>
                  {riders.map((r) => (
                    <tr key={r.race_number}>
                      <td className="mono">#{r.race_number}</td>
                      <td>{r.rider_first} {r.rider_last}</td>
                      <td className="num-col">{r.run_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button className="btn-sm" disabled={deleting}
              style={{ marginTop: 10, borderColor: 'var(--red)', color: 'var(--red)' }}
              onClick={remove}>
              {deleting ? 'Deleting…' : `Delete ${riders.reduce((n, r) => n + r.run_count, 0)} run(s)`}
            </button>
          </>
        )
      )}
    </div>
  )
}

function ClearDownPanel() {
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState('')
  const [error, setError] = useState('')

  const run = async () => {
    if (!window.confirm(
      "Clear all retained live-timing data from the broker?\n\n" +
      "Do this only after the event has fully finished — if today's event " +
      'is still being polled, the data will simply reappear within ~30 seconds.'
    )) return
    setBusy(true)
    setError('')
    setResult('')
    try {
      const data = await clearDown()
      setResult(`Cleared: ${(data.cleared || []).join(', ')}`)
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="admin-panel">
      <h3>Clear down</h3>
      <div className="desc">
        Removes every retained MQTT message (results, venue, latest, etc.) so a finished
        event's data doesn't linger on the live display for the next one.
      </div>
      <button className="btn-sm save" disabled={busy} onClick={run}>
        {busy ? 'Clearing…' : 'Clear down'}
      </button>
      {result && <div className="magic-sent">{result}</div>}
      {error && <div className="empty-state" style={{ color: 'var(--red)' }}>{error}</div>}
    </div>
  )
}

function ResultsEmailPanel({ hill, date }) {
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState('')
  const [error, setError] = useState('')

  const run = async () => {
    if (!window.confirm(`Email the results CSV for ${hill} ${date} now?`)) return
    setBusy(true)
    setError('')
    setResult('')
    try {
      const data = await sendResultsEmail(hill, date)
      setResult(`Sent ${data.filename} (${data.hill} ${data.date}) to ${data.to}`)
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="admin-panel">
      <h3>Send results CSV</h3>
      <div className="desc">
        Emails the selected event's TIMES_IMPORT CSV to the results address straight away,
        instead of waiting for the automatic 19:00 send on the day (which still happens as
        well). Works for past events too — but for a NIGAN-timed event only while its results
        are still on the broker, i.e. until the next NIGAN event or a clear-down.
      </div>
      <button className="btn-sm save" disabled={busy || !hill || !date} onClick={run}>
        {busy ? 'Sending…' : hill && date ? `Send ${hill} ${date}` : 'Select a hill and date above'}
      </button>
      {result && <div className="magic-sent">{result}</div>}
      {error && <div className="empty-state" style={{ color: 'var(--red)' }}>{error}</div>}
    </div>
  )
}

function ReplayPanel() {
  const [events, setEvents] = useState([])
  const [selectedHill, setSelectedHill] = useState('')
  const [selectedDate, setSelectedDate] = useState('')
  const [interval, setInterval_] = useState(3)
  const [status, setStatus] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    listReplayEvents().then((data) => setEvents(data.events || [])).catch((e) => setError(e.message))
  }, [])

  useEffect(() => {
    const poll = () => getReplayStatus().then(setStatus).catch(() => {})
    poll()
    const id = window.setInterval(poll, 2000)
    return () => window.clearInterval(id)
  }, [])

  // Hill first, then date scoped to that hill - a flat "hill — date" list
  // gets unwieldy as more events accumulate over a season. Preserves first-
  // appearance order from `events` (already event_date DESC from the
  // backend), so both dropdowns default to most-recently-seen-first.
  const hills = Array.from(new Set(events.map((ev) => ev.hill)))
  const datesForHill = events.filter((ev) => ev.hill === selectedHill).map((ev) => ev.date)

  const chooseHill = (h) => {
    setSelectedHill(h)
    setSelectedDate('')
  }

  const start = async () => {
    if (!selectedHill || !selectedDate) return
    setError('')
    try {
      await startReplay(selectedHill, selectedDate, Number(interval))
    } catch (e) {
      setError(e.message)
    }
  }

  const stop = async () => {
    setError('')
    try {
      await stopReplay()
    } catch (e) {
      setError(e.message)
    }
  }

  const running = !!status?.running

  return (
    <div className="admin-panel">
      <h3>Replay an event</h3>
      <div className="desc">
        Re-publishes a past event's runs to the live MQTT broker, round by round (practice,
        then timed, then top ten) and class/race-number within each round (live_runs has no
        per-run timestamp yet) — useful for testing the live display without a real event
        running. Refuses to start while today's event is actually live.
      </div>
      {error && <div className="empty-state" style={{ color: 'var(--red)' }}>{error}</div>}
      <div className="toolbar" style={{ alignItems: 'flex-end' }}>
        <div className="field" style={{ margin: 0, width: 220 }}>
          <label>Hill</label>
          <select value={selectedHill} onChange={(e) => chooseHill(e.target.value)} disabled={running}>
            <option value="">Select a hill…</option>
            {hills.map((h) => <option key={h} value={h}>{h}</option>)}
          </select>
        </div>
        <div className="field" style={{ margin: 0, width: 160 }}>
          <label>Date</label>
          <select value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}
                  disabled={running || !selectedHill}>
            <option value="">Select a date…</option>
            {datesForHill.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div className="field" style={{ margin: 0, width: 140 }}>
          <label>Interval (seconds)</label>
          <input
            type="number" min="1" step="0.5" value={interval} disabled={running}
            onChange={(e) => setInterval_(e.target.value)}
          />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button className="btn-sm save" disabled={!selectedHill || !selectedDate || running} onClick={start}>Start</button>
        <button className="btn-sm" disabled={!running} onClick={stop}>Stop</button>
      </div>
      {running && (
        <div className="magic-sent">
          Replaying {status.hill} {status.date}: {status.index}/{status.total}
          {status.current && ` — #${status.current.num} ${status.current.rider} (${status.current.session})`}
        </div>
      )}
      {status?.error && <div className="empty-state" style={{ color: 'var(--red)' }}>{status.error}</div>}
    </div>
  )
}

export default function AdminDashboard() {
  const { session, logoutLocal } = useAdminAuth()
  const navigate = useNavigate()
  const venue = useMqttTopic('venue')

  const [events, setEvents] = useState([])
  const [hill, setHill] = useState(venue?.venue || '')
  const [date, setDate] = useState(venue?.date || '')

  useEffect(() => {
    listReplayEvents().then((data) => setEvents(data.events || [])).catch(() => {})
  }, [])

  useEffect(() => {
    if (venue?.venue && !hill) setHill(venue.venue)
    if (venue?.date && !date) setDate(venue.date)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venue])

  // Picklist, not free text - a typo'd hill string is exactly how
  // "Gurston"/"Gurston Down" ended up split across live_runs. Sourced from
  // the same live_runs event history the Replay panel uses, plus the live
  // MQTT venue (if any) so a brand-new event with zero runs so far is still
  // selectable even before it has any live_runs rows to be discovered from.
  const hills = Array.from(new Set([
    ...(venue?.venue ? [venue.venue] : []),
    ...events.map((ev) => ev.hill),
  ]))
  const datesForHill = Array.from(new Set([
    ...(venue?.venue === hill && venue?.date ? [venue.date] : []),
    ...events.filter((ev) => ev.hill === hill).map((ev) => ev.date),
  ]))

  const chooseHill = (h) => {
    setHill(h)
    setDate('')
  }

  const doLogout = async () => {
    try { await apiLogout() } catch { /* best-effort */ }
    logoutLocal()
    navigate('/admin')
  }

  return (
    <div>
      <div className="section-head">
        <h2>Live Timing Admin</h2>
        <p>
          Signed in as {session?.email} — <button onClick={doLogout}
            style={{ background: 'none', border: 'none', color: 'var(--gulf-blue)', cursor: 'pointer', padding: 0, font: 'inherit', textDecoration: 'underline' }}>
            sign out
          </button>
        </p>
      </div>

      <div className="toolbar">
        <div className="field" style={{ margin: 0, width: 220 }}>
          <label>Hill</label>
          <select value={hill} onChange={(e) => chooseHill(e.target.value)}>
            <option value="">Select a hill…</option>
            {hills.map((h) => <option key={h} value={h}>{h}</option>)}
          </select>
        </div>
        <div className="field" style={{ margin: 0, width: 160 }}>
          <label>Date</label>
          <select value={date} onChange={(e) => setDate(e.target.value)} disabled={!hill}>
            <option value="">Select a date…</option>
            {datesForHill.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
      </div>

      {hill && date ? (
        <SessionsPanel hill={hill} date={date} />
      ) : (
        <div className="empty-state">Enter a hill and date to load this event's admin data.</div>
      )}

      <ResultsEmailPanel hill={hill} date={date} />

      <details className="advanced-panel">
        <summary>Advanced tools</summary>
        <div className="advanced-panel-warning">
          Don't use these unless you know what you're doing — they act on live MQTT data and
          can affect what's currently on the live display.
        </div>
        {hill && date && <RunsPanel hill={hill} date={date} />}
        {hill && date && <NonEntrantRunsPanel hill={hill} date={date} />}
        <ClearDownPanel />
        <ReplayPanel />
      </details>
    </div>
  )
}
